import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CurrentUser } from '@/core/models/user';
import { UserService } from '@/core/services/open-api/user.service';
import { WizardService } from '../../services/wizard.service';
import { SharedFunctions } from '@/core/utils/shared-functions';
import { TranslateService } from '@ngx-translate/core';
import { SelectOption } from '@/core/types/angular-primeng-api';
import { combineLatest, Subscription, take } from 'rxjs';
import { SelectChangeEvent } from 'primeng/types/select';
import { TranslationManagerService } from '@/core/services/translation-manager.service';
import { UserSettings } from '@/core/models/user-settings';
import { UserUpdateErrorService } from '@/core/services/user-update-error.service';
import { ToastService } from '@/core/services/toast.service';

@Component({
    selector: 'app-wizard-first',
    templateUrl: './wizard-first.component.html',
    // styleUrls: ['./wizard-first.component.scss'],
    standalone: false,
})
export class WizardFirstComponent implements OnInit, OnDestroy {
    @Input() firstName!: string;
    @Input() lastName!: string;
    @Output() firstNameChange = new EventEmitter<string>();
    @Output() lastNameChange = new EventEmitter<string>();
    private me?: CurrentUser;
    password: string = '';
    firstNameInvalid = false;
    lastNameInvalid = false;
    languages: SelectOption[] = [];
    selectedLanguageCode?: string;
    subscriptions = new Subscription();

    constructor(
        private userService: UserService,
        private readonly translate: TranslateService,
        private wizardService: WizardService,
        private readonly translationManager: TranslationManagerService,
        private readonly userUpdateError: UserUpdateErrorService,
        private readonly toastService: ToastService,
    ) {}

    ngOnInit(): void {
        const me$ = this.userService.loadCurrentUser().pipe(take(1));
        const languages$ = this.translate.get('languages').pipe(take(1)); // lädt damit auch die aktuelle Sprache

        this.subscriptions.add(
            combineLatest([me$, languages$]).subscribe(([me, _languages]) => {
                this.me = me;
                this.languages = SharedFunctions.getLanguageOptions(this.translate);
                this.selectedLanguageCode = me.settings.language || undefined;
            }),
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    changeLanguage(event: SelectChangeEvent): void {
        if (!this.me?.settings) {
            return;
        }
        this.me.settings.language = event.value;
        this.translationManager.setUserLanguage(this.me);
    }

    goToNext(): void {
        // Save username and password to profile
        if (!this.me) {
            // ngOnInit loads the current user before the step can be completed, so this should not happen
            this.toastService.error('standard_error', 'standard_unexpected_error_retry');
            return;
        }
        this.subscriptions.add(
            this.userService
                .patchCurrentUser(
                    {
                        firstName: this.firstName,
                        lastName: this.lastName,
                        settings: UserSettings.toJson(this.me.settings),
                    },
                    this.password,
                )
                .subscribe({
                    next: () => {
                        if (!this.wizardService.isLastStep()) {
                            this.wizardService.moveToNextStep();
                        }
                    },
                    error: (error) => this.userUpdateError.show(error, 'profile_profilesavingerror'),
                }),
        );
    }

    validateFirstName(_event: FocusEvent): void {
        this.firstNameInvalid = this.firstName.length < 2;
    }

    validateLastName(_event: FocusEvent): void {
        this.lastNameInvalid = this.lastName.length < 2;
    }

    onFirstNameChange(value: string): void {
        this.firstNameChange.emit(value);
    }

    onLastNameChange(value: string): void {
        this.lastNameChange.emit(value);
    }
}
